import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, Check, X, Key, Loader2, Combine, ArrowRight } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MultiSelect from "@/components/MultiSelect";

const ALL_PERMISSIONS = [
  "upload",
  "users:manage",
  "dept:manage",
  "employees:manage",
  "share:manage",
  "report:kpis",
  "report:ranking",
  "report:comparativo",
  "report:tendencia",
  "report:distribucion",
  "report:heatmap",
  "report:tabla",
  "report:efficiency",
  "report:3d",
  "report:ganador",
  "schedule:view",
  "schedule:manage",
  "catalog:view",
  "catalog:manage",
];

const emptyDraft = {
  id: null,
  email: "",
  name: "",
  password: "",
  role: "viewer",
  permissions: [],
  department_ids: [],
  active: true,
};

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: me, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [draft, setDraft] = useState(emptyDraft);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pwdDialog, setPwdDialog] = useState(null); // user object
  const [newPwd, setNewPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const deptOptions = useMemo(
    () => depts.map((d) => ({ value: d.id, label: d.name })),
    [depts]
  );
  const permOptions = useMemo(
    () =>
      ALL_PERMISSIONS.map((p) => ({
        value: p,
        label: t(`perms.${p}`),
      })),
    [t]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([api.get("/users"), api.get("/departments")]);
      setUsers(u.data);
      setDepts(d.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startEdit = (u) => {
    setEditing(u.id);
    setDraft({
      id: u.id,
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      permissions: u.permissions || [],
      department_ids: u.department_ids || [],
      active: u.active,
    });
  };

  const startNew = () => {
    setEditing("new");
    setDraft({ ...emptyDraft });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(emptyDraft);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        if (!draft.password || draft.password.length < 6) {
          throw new Error("Contraseña mínima 6 caracteres");
        }
        const { data } = await api.post("/users", {
          email: draft.email.trim().toLowerCase(),
          name: draft.name.trim(),
          password: draft.password,
          role: draft.role,
          permissions: draft.permissions,
          department_ids: draft.department_ids,
          active: draft.active,
        });
        setUsers((curr) => [data, ...curr]);
        toast.success(t("users.created"));
      } else {
        const payload = {
          email: draft.email.trim().toLowerCase(),
          name: draft.name.trim(),
          role: draft.role,
          permissions: draft.permissions,
          department_ids: draft.department_ids,
          active: draft.active,
        };
        const { data } = await api.patch(`/users/${draft.id}`, payload);
        setUsers((curr) => curr.map((u) => (u.id === data.id ? data : u)));
        toast.success(t("users.updated"));
      }
      cancelEdit();
    } catch (err) {
      toast.error(err?.message || formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.id === me?.id) {
      toast.error(t("users.cannotDeleteSelf"));
      setConfirmDelete(null);
      return;
    }
    try {
      const { data } = await api.delete(`/users/${confirmDelete.id}`);
      setUsers((curr) => curr.map((u) => (u.id === data.id ? data : u)));
      toast.success(t("users.deleted"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirmDelete(null);
    }
  };

  const doPasswordChange = async () => {
    if (!pwdDialog) return;
    if (!newPwd || newPwd.length < 6) {
      toast.error("Mínimo 6 caracteres");
      return;
    }
    try {
      await api.patch(`/users/${pwdDialog.id}`, { password: newPwd });
      toast.success(t("users.passwordChanged"));
      setPwdDialog(null);
      setNewPwd("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const renderEditingRow = () => (
    <TableRow data-testid="user-edit-row" className="bg-accent/30">
      <TableCell>
        <Input
          data-testid="user-email-input"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          placeholder="email@bodega.com"
        />
      </TableCell>
      <TableCell>
        <Input
          data-testid="user-name-input"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Nombre"
        />
      </TableCell>
      <TableCell>
        <Select
          value={draft.role}
          onValueChange={(v) => setDraft({ ...draft, role: v })}
        >
          <SelectTrigger data-testid="user-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">admin</SelectItem>
            <SelectItem value="viewer">viewer</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="min-w-[14rem]">
        <MultiSelect
          options={permOptions}
          value={draft.permissions}
          onChange={(v) => setDraft({ ...draft, permissions: v })}
          placeholder={t("common.none")}
          testId="user-perms-select"
        />
      </TableCell>
      <TableCell className="min-w-[14rem]">
        <MultiSelect
          options={deptOptions}
          value={draft.department_ids}
          onChange={(v) => setDraft({ ...draft, department_ids: v })}
          placeholder={t("common.all")}
          testId="user-depts-select"
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={draft.active}
          onCheckedChange={(v) => setDraft({ ...draft, active: v })}
          data-testid="user-active-switch"
        />
      </TableCell>
      <TableCell>
        {editing === "new" && (
          <Input
            type="password"
            placeholder={t("common.password")}
            value={draft.password}
            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            data-testid="user-password-input"
            className="mb-2"
          />
        )}
        <div className="flex gap-1">
          <Button
            size="sm"
            onClick={saveDraft}
            disabled={saving}
            data-testid="user-save-btn"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEdit}
            disabled={saving}
            data-testid="user-cancel-btn"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="users-page-title"
          >
            {t("users.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("users.subtitle")}</p>
        </div>
        <Button
          onClick={startNew}
          disabled={editing === "new"}
          data-testid="add-user-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> {t("users.addUser")}
        </Button>
      </div>

      {hasPermission?.("employees:manage") && (
        <Link
          to="/admin/employees"
          className="group flex items-center justify-between gap-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 transition-colors hover:bg-primary/10"
          data-testid="goto-employees-canonical-banner"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Combine className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {t("users.gotoCanonicalTitle")}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("users.gotoCanonicalDesc")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
            {t("users.gotoCanonicalCta")}
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </Link>
      )}

      <Card data-testid="users-table-card">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.role")}</TableHead>
                    <TableHead>{t("common.permissions")}</TableHead>
                    <TableHead>{t("common.departments")}</TableHead>
                    <TableHead>{t("common.active")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editing === "new" && renderEditingRow()}
                  {users.length === 0 && editing !== "new" && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-sm text-muted-foreground"
                      >
                        {t("users.noUsers")}
                      </TableCell>
                    </TableRow>
                  )}
                  {users.map((u) =>
                    editing === u.id ? (
                      <React.Fragment key={u.id}>{renderEditingRow()}</React.Fragment>
                    ) : (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>
                          <Badge
                            variant={u.role === "admin" ? "default" : "secondary"}
                            className="font-mono text-xs"
                          >
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(u.permissions || []).slice(0, 3).map((p) => (
                              <Badge
                                key={p}
                                variant="outline"
                                className="font-mono text-[10px]"
                              >
                                {p}
                              </Badge>
                            ))}
                            {(u.permissions || []).length > 3 && (
                              <Badge variant="outline" className="text-[10px]">
                                +{u.permissions.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(u.department_ids || []).map((id) => {
                              const d = depts.find((x) => x.id === id);
                              return d ? (
                                <Badge
                                  key={id}
                                  variant="outline"
                                  style={{ borderColor: d.color }}
                                  className="text-[10px]"
                                >
                                  {d.name}
                                </Badge>
                              ) : null;
                            })}
                            {(u.department_ids || []).length === 0 && (
                              <span className="text-xs text-muted-foreground">
                                {u.role === "admin" ? t("common.all") : t("common.none")}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.active ? (
                            <Badge
                              className="bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/20"
                              data-testid={`user-active-${u.id}`}
                            >
                              {t("common.active")}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              {t("common.inactive")}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => startEdit(u)}
                              data-testid={`edit-user-${u.id}`}
                              title={t("common.edit")}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setPwdDialog(u)}
                              data-testid={`change-password-${u.id}`}
                              title={t("users.changePassword")}
                            >
                              <Key className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setConfirmDelete(u)}
                              disabled={u.id === me?.id || !u.active}
                              data-testid={`delete-user-${u.id}`}
                              title={t("common.delete")}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirm */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>{t("common.confirm")}</DialogTitle>
            <DialogDescription>
              {confirmDelete &&
                t("users.confirmDelete", { name: confirmDelete.name || confirmDelete.email })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              data-testid="confirm-delete-user-btn"
            >
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password change dialog */}
      <Dialog
        open={!!pwdDialog}
        onOpenChange={(o) => {
          if (!o) {
            setPwdDialog(null);
            setNewPwd("");
          }
        }}
      >
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>{t("users.changePassword")}</DialogTitle>
            <DialogDescription>{pwdDialog?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-pwd">{t("users.newPassword")}</Label>
            <Input
              id="new-pwd"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPwdDialog(null);
                setNewPwd("");
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={doPasswordChange} data-testid="confirm-password-btn">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
